import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const CANCEL_REASONS = [
  { value: "returned_already", label: "Returned already" },
  { value: "no_longer_returning", label: "No longer returning" },
  { value: "deadline_missed", label: "Return deadline missed" },
  { value: "other", label: "Other" },
] as const;

interface CancelPackageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => void;
  loading?: boolean;
  packageRetailer?: string;
  isPartOfScheduledPickup?: boolean;
}

export const CancelPackageDialog = ({
  open,
  onOpenChange,
  onConfirm,
  loading = false,
  packageRetailer,
  isPartOfScheduledPickup = false,
}: CancelPackageDialogProps) => {
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [otherReason, setOtherReason] = useState("");

  const handleConfirm = () => {
    let finalReason = "";
    if (selectedReason === "other") {
      finalReason = otherReason.trim() || "Other";
    } else {
      const reasonLabel = CANCEL_REASONS.find(r => r.value === selectedReason)?.label;
      finalReason = reasonLabel || selectedReason;
    }
    onConfirm(finalReason);
    resetForm();
  };

  const resetForm = () => {
    setSelectedReason("");
    setOtherReason("");
  };

  const handleCancel = () => {
    resetForm();
    onOpenChange(false);
  };

  const isConfirmDisabled = !selectedReason || (selectedReason === "other" && !otherReason.trim()) || loading;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Cancel This Package?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-left">
              <p>
                Canceling this package will remove it from your upcoming pickup.
              </p>
              <p className="font-medium text-foreground">
                This action cannot be undone.
              </p>
              {isPartOfScheduledPickup && (
                <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg border">
                  This will not cancel your scheduled pickup unless no packages remain.
                </p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="py-4 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="cancel-reason" className="text-sm font-medium">
              Reason for cancellation <span className="text-red-500">*</span>
            </Label>
            <Select value={selectedReason} onValueChange={setSelectedReason}>
              <SelectTrigger id="cancel-reason" className="w-full">
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent>
                {CANCEL_REASONS.map((reason) => (
                  <SelectItem key={reason.value} value={reason.value}>
                    {reason.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedReason === "other" && (
            <div className="space-y-2">
              <Label htmlFor="other-reason" className="text-sm font-medium">
                Please specify <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="other-reason"
                placeholder="Tell us why you're cancelling this package..."
                value={otherReason}
                onChange={(e) => setOtherReason(e.target.value)}
                rows={3}
                maxLength={500}
              />
              <p className="text-xs text-muted-foreground text-right">
                {otherReason.length}/500
              </p>
            </div>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleCancel} disabled={loading}>
            Keep Package
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isConfirmDisabled}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {loading ? "Cancelling..." : "Cancel Package"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
